import { ApiFragment, FragmentId, MediaType, MH2MediaType, RecordTypeStructure } from '@stylehaven/components';
import { trimEnd } from 'lodash-es';

const RichEmbedTypes: MH2MediaType[] = [
    MediaType.video,
    MediaType.videofragment,
    MediaType.audio,
    MediaType.audiofragment,
    MediaType.collection,
    MediaType.set,
    MediaType.filearchive
];

/**
 *  <iframe
 *      src="https://offline.mediahaven.com/views/rich/11750b0c2b7744418dd972ffb18095f51963f56621ec4f4e8e9cd3193e6d4b0382ca58b485b14e67829087286208ed18"
 *      width="100"
 *      height="100"
 *      frameborder="0"
 *      scrolling="no"
 *      allowfullscreen
 *  ></iframe>
 *
 *   <img
 *      src="https://offline.mediahaven.com/views/direct/11750b0c2b7744418dd972ffb18095f51963f56621ec4f4e8e9cd3193e6d4b0382ca58b485b14e67829087286208ed18?w=100&h=100"
 *      width="100"
 *      height="100"
 *  ></img>
 */
export function GenerateEmbedCode(
    baseEmbedUrl: string,
    record: ApiFragment,
    width: number | null,
    height: number | null,
    autoplay?: boolean
): string {
    const base: string = trimEnd(baseEmbedUrl, '/');

    if (IsDirectEmbed(record)) {
        // MED-4932: Non-DataFlat documents should generate Iframes
        if (isNonFlatDocument(record)) {
            return generateIframeCode(base, record, width, height, autoplay);
        }
        return generateImgCode(base, record, width, height);
    }
    return generateIframeCode(base, record, width, height, autoplay);
}

export function IsEmbedable(record: ApiFragment): boolean {
    const type: MH2MediaType = record.getType();
    const structure: RecordTypeStructure = record.getRecordStructure();

    // Only processing, filearchive and metadataonly objects that are not Intellectual types can not be embedded
    return !(
        type === MediaType.processing ||
        type === MediaType.filearchive ||
        (type === MediaType.metadataonly && structure !== RecordTypeStructure.Intellectual)
    );
}

export function IsDirectEmbed(record: ApiFragment): boolean {
    const type: MH2MediaType = record.getType();
    const structure: RecordTypeStructure = record.getRecordStructure();

    // Intellectual types are always rich embed
    if (structure === RecordTypeStructure.Intellectual) {
        return false;
    }

    return !RichEmbedTypes.includes(type);
}

export function GenerateEmbedUrl(
    baseEmbedUrl: string,
    record: ApiFragment,
    width: number | null,
    height: number | null,
    autoplay?: boolean
): string {
    const base: string = trimEnd(baseEmbedUrl, '/');

    if (IsDirectEmbed(record)) {
        return generateDirectUrl(base, record, width, height);
    }
    return generateRichUrl(base, record, autoplay);
}

function generateDirectUrl(base: string, record: ApiFragment, width: number | null, height: number | null): string {
    const fragmentId: FragmentId = record.getFragmentId();

    const params: string[] = [];
    const source = `${base}/direct/${fragmentId}`;

    if (!!width && width > 0) {
        params.push(`w=${width}`);
    }
    if (!!height && height > 0) {
        params.push(`h=${height}`);
    }

    const paramsString: string = params.length > 0 ? `?${params.join('&')}` : '';

    return `${source}${paramsString}`;
}

function generateImgCode(base: string, record: ApiFragment, width: number | null, height: number | null): string {
    const attributes: string[] = [];

    if (!!width && width > 0) {
        attributes.push(`width="${width}"`);
    }
    if (!!height && height > 0) {
        attributes.push(`height="${height}"`);
    }

    const attributeString: string = attributes.length > 0 ? `${attributes.join(' ')}` : '';

    const src: string = generateDirectUrl(base, record, width, height);

    return `<img src="${src}" ${attributeString} alt=""/>`;
}

function generateRichUrl(base: string, record: ApiFragment, autoplay?: boolean): string {
    const fragmentId: FragmentId = record.getFragmentId();

    const params: string[] = [];
    const source = `${base}/rich/${fragmentId}`;

    if (autoplay) {
        params.push('autoplay=true');
    }

    const paramsString: string = params.length > 0 ? `?${params.join('&')}` : '';

    return `${source}${paramsString}`;
}

function generateIframeCode(
    base: string,
    record: ApiFragment,
    width: number | null,
    height: number | null,
    autoplay?: boolean
): string {
    const attributes: string[] = ['frameborder="0"', 'scrolling="no"', 'allowfullscreen'];

    if (!!width && width > 0) {
        attributes.push(`width="${width}"`);
    }
    if (!!height && height > 0) {
        attributes.push(`height="${height}"`);
    }

    const attributeString: string = attributes.length > 0 ? `${attributes.join(' ')}` : '';

    // MED-4932: Could also be Direct url (for docs for example)
    const src: string = GenerateEmbedUrl(base, record, width, height, autoplay);

    return `<iframe src="${src}" ${attributeString}></iframe>`;
}

function isNonFlatDocument(record: ApiFragment): boolean {
    return (
        record.getMediaHavenType() === MediaType.document &&
        record.getRecordStructure() !== RecordTypeStructure.DataFlat
    );
}
